// frontend/src/pages/ShipOwnerReportPage.tsx
// Версия: 2.0.0 (2026-03-04)
// FIX v1.0.1: sessionStorage+localStorage fallback (Grok#2 audit)
// FIX v1.1.0: GPT audit — fetch+blob для PDF (JWT-совместимость),
//   убраны лишние импорты, pdfUrl через apiUrl()
// FIX v1.2.0: три действия с PDF — Просмотр / Скачать / Печать
// FIX v2.0.0: режим "По судовладельцу" — Autocomplete owner → все суда → тот же отчёт
// Страница формирования отчётов по запросам судовладельцев
// Путь в проекте: ~/ssto-project/frontend/src/pages/ShipOwnerReportPage.tsx

import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  Autocomplete,
  Chip,
  FormGroup,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import BusinessIcon from '@mui/icons-material/Business';
import { apiUrl } from '../config/paths';

// ═══════════════════════════════════════════════════════════════
// ТИПЫ
// ═══════════════════════════════════════════════════════════════

interface VesselOption {
  id: number;
  imo: string;
  mmsi?: string;
  vesselName: string;
  vesselNameRu?: string;
  callSign?: string;
  inmarsatC?: string;
  iridium?: string;
  flag?: string;
  owner?: string;
  displayName: string;
}

interface ReportResultVessel {
  imo: string;
  vesselName: string;
  vesselNameEn?: string;
  activationsCount: number;
}

interface ReportResult {
  reportId: number;
  generatedAt: string;
  period: { from: string; to: string };
  totalVessels: number;
  totalActivations: number;
  byType: Record<string, number>;
  vessels: ReportResultVessel[];
  pdfUrl: string;
}

interface HistoryItem {
  id: number;
  requester_org: string;
  requester_name: string;
  date_from: string;
  date_to: string;
  imo_numbers: string[];
  created_at: string;
  total_activations: number;
}

interface OwnerOption {
  owner: string;
  vesselCount: number;
  imoList: string[];
}

type SearchMode = 'vessels' | 'owner';

// Маппинг типов срабатываний
const SIGNAL_TYPES = [
  { value: 'SSAS_TEST', label: 'Тестовое (SSAS_TEST)' },
  { value: 'SSAS_ALERT', label: 'Боевое (SSAS_ALERT)' },
  { value: 'TEST', label: 'Проверочное (TEST)' },
  { value: 'FALSE_ALARM', label: 'Ложное (FALSE_ALARM)' },
  { value: 'ERROR', label: 'Ошибочное (ERROR)' },
  { value: 'REQUEST_ONLY', label: 'Заявка без сигнала (REQUEST_ONLY)' }, // FIX GPT#13
];

// ═══════════════════════════════════════════════════════════════
// FIX GPT#3: Работа с PDF через fetch+blob (JWT-совместимо)
// ═══════════════════════════════════════════════════════════════

/** Общий хелпер: получить blob PDF по reportId */
const fetchPdfBlob = async (reportId: number): Promise<Blob> => {
  const token = sessionStorage.getItem('token') || localStorage.getItem('token') || '';
  const response = await fetch(apiUrl(`shipowner-reports/${reportId}/pdf`), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    throw new Error(`Ошибка загрузки PDF: ${response.status}`);
  }
  return response.blob();
};

/** Скачать PDF (сохранить файл на диск) */
const downloadPdf = async (reportId: number): Promise<void> => {
  const blob = await fetchPdfBlob(reportId);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `shipowner_report_${reportId}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/** Просмотр PDF на экране (открытие в новой вкладке) */
const previewPdf = async (reportId: number): Promise<void> => {
  const blob = await fetchPdfBlob(reportId);
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  // Не revoke — браузеру нужен URL пока вкладка открыта
};

/** Печать PDF (через скрытый iframe) */
const printPdf = async (reportId: number): Promise<void> => {
  const blob = await fetchPdfBlob(reportId);
  const url = URL.createObjectURL(blob);
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.src = url;
  document.body.appendChild(iframe);
  iframe.onload = () => {
    iframe.contentWindow?.print();
    // Удаляем iframe после небольшой задержки (чтобы диалог печати успел открыться)
    setTimeout(() => {
      document.body.removeChild(iframe);
      URL.revokeObjectURL(url);
    }, 3000);
  };
};

// ═══════════════════════════════════════════════════════════════
// КОМПОНЕНТ
// ═══════════════════════════════════════════════════════════════

const ShipOwnerReportPage: React.FC = () => {
  // Режим поиска: по судам или по судовладельцу
  const [searchMode, setSearchMode] = useState<SearchMode>('vessels');

  // Состояние формы — поиск по судам
  const [selectedVessels, setSelectedVessels] = useState<VesselOption[]>([]);
  const [vesselSearchInput, setVesselSearchInput] = useState('');
  const [vesselOptions, setVesselOptions] = useState<VesselOption[]>([]);
  const [vesselLoading, setVesselLoading] = useState(false);

  // Состояние формы — поиск по судовладельцу
  const [ownerSearchInput, setOwnerSearchInput] = useState('');
  const [ownerOptions, setOwnerOptions] = useState<OwnerOption[]>([]);
  const [ownerLoading, setOwnerLoading] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState<OwnerOption | null>(null);
  const [ownerVessels, setOwnerVessels] = useState<VesselOption[]>([]);
  const [ownerVesselsLoading, setOwnerVesselsLoading] = useState(false);

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedSignalTypes, setSelectedSignalTypes] = useState<string[]>([]);
  const [requesterOrg, setRequesterOrg] = useState('');
  const [requesterName, setRequesterName] = useState('');
  const [requesterPosition, setRequesterPosition] = useState('');
  const [purpose, setPurpose] = useState('');

  // Состояние результата
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState<ReportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // История
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // ═══════════════════════════════════════════════════════════
  // ПОИСК СУДОВ (debounced)
  // ═══════════════════════════════════════════════════════════
  useEffect(() => {
    if (vesselSearchInput.length < 2) {
      setVesselOptions([]);
      return;
    }

    const timer = setTimeout(async () => {
      setVesselLoading(true);
      try {
        const response = await fetch(
          apiUrl(`shipowner-reports/vessels/search?q=${encodeURIComponent(vesselSearchInput)}`),
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem('token') || localStorage.getItem('token') || ''}`,
            },
          }
        );
        const json = await response.json();
        if (json.success && Array.isArray(json.data)) {
          setVesselOptions(json.data as VesselOption[]);
        }
      } catch (err) {
        console.error('Ошибка поиска судов:', err);
      } finally {
        setVesselLoading(false);
      }
    }, 400); // debounce 400ms

    return () => clearTimeout(timer);
  }, [vesselSearchInput]);

  // ═══════════════════════════════════════════════════════════
  // ПОИСК СУДОВЛАДЕЛЬЦЕВ (debounced)
  // ═══════════════════════════════════════════════════════════
  useEffect(() => {
    if (ownerSearchInput.length < 2) {
      setOwnerOptions([]);
      return;
    }

    const timer = setTimeout(async () => {
      setOwnerLoading(true);
      try {
        const response = await fetch(
          apiUrl(`shipowner-reports/owners/search?q=${encodeURIComponent(ownerSearchInput)}`),
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem('token') || localStorage.getItem('token') || ''}`,
            },
          }
        );
        const json = await response.json();
        if (json.success && Array.isArray(json.data)) {
          setOwnerOptions(json.data as OwnerOption[]);
        }
      } catch (err) {
        console.error('Ошибка поиска судовладельцев:', err);
      } finally {
        setOwnerLoading(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [ownerSearchInput]);

  // ═══════════════════════════════════════════════════════════
  // ЗАГРУЗКА СУДОВ ПО СУДОВЛАДЕЛЬЦУ
  // ═══════════════════════════════════════════════════════════
  useEffect(() => {
    if (!selectedOwner) {
      setOwnerVessels([]);
      return;
    }

    let cancelled = false;
    (async () => {
      setOwnerVesselsLoading(true);
      try {
        const response = await fetch(
          apiUrl(
            `shipowner-reports/vessels/by-owner?owner=${encodeURIComponent(selectedOwner.owner)}`
          ),
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem('token') || localStorage.getItem('token') || ''}`,
            },
          }
        );
        const json = await response.json();
        if (!cancelled && json.success && Array.isArray(json.data)) {
          setOwnerVessels(json.data as VesselOption[]);
        }
      } catch (err) {
        console.error('Ошибка загрузки судов владельца:', err);
      } finally {
        if (!cancelled) setOwnerVesselsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedOwner]);

  // ═══════════════════════════════════════════════════════════
  // РУЧНОЙ ВВОД IMO
  // ═══════════════════════════════════════════════════════════
  const [manualImo, setManualImo] = useState('');

  const handleAddManualImo = useCallback(() => {
    const imo = manualImo.trim();
    if (!/^\d{7}$/.test(imo)) {
      setError('IMO номер должен состоять из 7 цифр');
      return;
    }
    // Проверяем дубль
    if (selectedVessels.some((v) => v.imo === imo)) {
      setError(`Судно IMO ${imo} уже добавлено`);
      return;
    }
    setSelectedVessels((prev) => [
      ...prev,
      {
        id: -Date.now(), // временный ID
        imo,
        vesselName: `Судно IMO ${imo}`,
        displayName: `IMO ${imo} (введено вручную)`,
      },
    ]);
    setManualImo('');
    setError(null);
  }, [manualImo, selectedVessels]);

  // ═══════════════════════════════════════════════════════════
  // ПЕРЕКЛЮЧЕНИЕ ТИПОВ СИГНАЛОВ
  // ═══════════════════════════════════════════════════════════
  const handleToggleSignalType = useCallback((typeValue: string) => {
    setSelectedSignalTypes((prev) =>
      prev.includes(typeValue) ? prev.filter((t) => t !== typeValue) : [...prev, typeValue]
    );
  }, []);

  // ═══════════════════════════════════════════════════════════
  // ГЕНЕРАЦИЯ ОТЧЁТА
  // ═══════════════════════════════════════════════════════════
  const handleGenerate = useCallback(async () => {
    // Определяем список IMO в зависимости от режима
    const effectiveVessels = searchMode === 'owner' ? ownerVessels : selectedVessels;

    // Валидация
    if (searchMode === 'owner' && !selectedOwner) {
      setError('Выберите судовладельца');
      return;
    }
    if (effectiveVessels.length === 0) {
      setError(
        searchMode === 'owner'
          ? 'У выбранного судовладельца нет судов с IMO'
          : 'Выберите хотя бы одно судно'
      );
      return;
    }
    if (!dateFrom || !dateTo) {
      setError('Укажите период (дату начала и окончания)');
      return;
    }
    if (new Date(dateFrom) > new Date(dateTo)) {
      setError('Дата начала не может быть позже даты окончания');
      return;
    }

    setError(null);
    setGenerating(true);
    setResult(null);

    try {
      const operatorInfo = localStorage.getItem('operator_info');
      let operatorName: string | undefined;
      try {
        const parsed = operatorInfo ? JSON.parse(operatorInfo) : null;
        operatorName = parsed?.shortName || parsed?.fullName || undefined;
      } catch {
        /* ignore */
      }

      const body = {
        imoNumbers: effectiveVessels.map((v) => v.imo),
        dateFrom,
        dateTo,
        signalTypes: selectedSignalTypes.length > 0 ? selectedSignalTypes : undefined,
        requesterOrg: requesterOrg || (searchMode === 'owner' ? selectedOwner?.owner : undefined),
        requesterName: requesterName || undefined,
        requesterPosition: requesterPosition || undefined,
        purpose: purpose || undefined,
        operatorName,
      };

      const response = await fetch(apiUrl('shipowner-reports/generate'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionStorage.getItem('token') || localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify(body),
      });

      const json = await response.json();
      if (json.success) {
        setResult(json.data as ReportResult);
      } else {
        setError(json.error || 'Ошибка генерации отчёта');
      }
    } catch (err) {
      setError(`Ошибка сети: ${err}`);
    } finally {
      setGenerating(false);
    }
  }, [
    searchMode,
    selectedVessels,
    ownerVessels,
    selectedOwner,
    dateFrom,
    dateTo,
    selectedSignalTypes,
    requesterOrg,
    requesterName,
    requesterPosition,
    purpose,
  ]);

  // ═══════════════════════════════════════════════════════════
  // ЗАГРУЗКА ИСТОРИИ
  // ═══════════════════════════════════════════════════════════
  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const response = await fetch(apiUrl('shipowner-reports/history?limit=20'), {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem('token') || localStorage.getItem('token') || ''}`,
        },
      });
      const json = await response.json();
      if (json.success && json.data?.rows) {
        setHistory(json.data.rows as HistoryItem[]);
      }
    } catch (err) {
      console.error('Ошибка загрузки истории:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const handleToggleHistory = useCallback(() => {
    if (!showHistory) {
      loadHistory();
    }
    setShowHistory((prev) => !prev);
  }, [showHistory, loadHistory]);

  // ═══════════════════════════════════════════════════════════
  // РЕНДЕР
  // ═══════════════════════════════════════════════════════════
  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        Отчёты по запросам судовладельцев
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Формирование отчётов о срабатываниях ССТО/SSAS по запросам судоходных компаний. Выберите
        суда из базы данных или введите IMO номера вручную.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ═══════════════ БЛОК ВЫБОРА СУДОВ ═══════════════ */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          1. Выбор судов
        </Typography>

        {/* Переключатель режима поиска */}
        <RadioGroup
          row
          value={searchMode}
          onChange={(e) => {
            setSearchMode(e.target.value as SearchMode);
            setError(null);
          }}
          sx={{ mb: 2 }}
        >
          <FormControlLabel
            value="vessels"
            control={<Radio size="small" />}
            label="По судам (IMO / название)"
          />
          <FormControlLabel
            value="owner"
            control={<Radio size="small" />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <BusinessIcon fontSize="small" />
                По судовладельцу
              </Box>
            }
          />
        </RadioGroup>

        {/* ─── Режим: По судам ─── */}
        {searchMode === 'vessels' && (
          <>
            <Grid container spacing={2} alignItems="flex-start">
              {/* Автокомплит из БД */}
              <Grid item xs={12} md={8}>
                <Autocomplete
                  multiple
                  options={vesselOptions}
                  getOptionLabel={(opt) => opt.displayName || `IMO ${opt.imo}`}
                  value={selectedVessels}
                  onChange={(_event, newValue) => setSelectedVessels(newValue)}
                  inputValue={vesselSearchInput}
                  onInputChange={(_event, newInput) => setVesselSearchInput(newInput)}
                  loading={vesselLoading}
                  isOptionEqualToValue={(opt, val) => opt.imo === val.imo}
                  filterSelectedOptions
                  noOptionsText={
                    vesselSearchInput.length < 2
                      ? 'Введите 2+ символа для поиска'
                      : 'Судна не найдены'
                  }
                  renderOption={(props, option) => (
                    <li {...props} key={option.imo}>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {option.vesselNameRu || option.vesselName}
                          {option.vesselNameRu &&
                          option.vesselName &&
                          option.vesselNameRu !== option.vesselName
                            ? ` (${option.vesselName})`
                            : ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          IMO: {option.imo || 'N/A'} | MMSI: {option.mmsi || 'N/A'}
                          {option.inmarsatC ? ` | Inmarsat-C: ${option.inmarsatC}` : ''}
                          {option.flag ? ` | ${option.flag}` : ''}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  renderTags={(tagValue, getTagProps) =>
                    tagValue.map((opt, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={opt.imo}
                        label={`${opt.vesselNameRu || opt.vesselName} (IMO ${opt.imo})`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Поиск судна (IMO, название RU/EN, MMSI)"
                      placeholder="Начните вводить..."
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Ручной ввод IMO */}
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    label="IMO (7 цифр)"
                    size="small"
                    value={manualImo}
                    onChange={(e) => setManualImo(e.target.value.replace(/\D/g, '').slice(0, 7))}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddManualImo()}
                    sx={{ flex: 1 }}
                    inputProps={{ maxLength: 7 }}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleAddManualImo}
                    disabled={manualImo.length !== 7}
                  >
                    Добавить
                  </Button>
                </Box>
              </Grid>
            </Grid>

            {selectedVessels.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Выбрано судов: {selectedVessels.length}
              </Typography>
            )}
          </>
        )}

        {/* ─── Режим: По судовладельцу ─── */}
        {searchMode === 'owner' && (
          <>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} md={8}>
                <Autocomplete
                  options={ownerOptions}
                  getOptionLabel={(opt) => `${opt.owner} (${opt.vesselCount} судов)`}
                  value={selectedOwner}
                  onChange={(_event, newValue) => setSelectedOwner(newValue)}
                  inputValue={ownerSearchInput}
                  onInputChange={(_event, newInput) => setOwnerSearchInput(newInput)}
                  loading={ownerLoading}
                  isOptionEqualToValue={(opt, val) => opt.owner === val.owner}
                  noOptionsText={
                    ownerSearchInput.length < 2
                      ? 'Введите 2+ символа для поиска'
                      : 'Судовладельцы не найдены'
                  }
                  renderOption={(props, option) => (
                    <li {...props} key={option.owner}>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          <BusinessIcon
                            fontSize="small"
                            sx={{ mr: 0.5, verticalAlign: 'text-bottom' }}
                          />
                          {option.owner}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Судов: {option.vesselCount}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Поиск судовладельца"
                      placeholder="Введите название организации..."
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <BusinessIcon sx={{ color: 'action.active', mr: 1 }} />
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>

            {/* Список судов выбранного владельца */}
            {selectedOwner && (
              <Box sx={{ mt: 2 }}>
                {ownerVesselsLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2">Загрузка судов...</Typography>
                  </Box>
                ) : (
                  <>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Суда «{selectedOwner.owner}» ({ownerVessels.length}):
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {ownerVessels.map((v) => (
                        <Chip
                          key={v.imo}
                          label={`${v.vesselNameRu || v.vesselName} (IMO ${v.imo})`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </>
                )}
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* ═══════════════ БЛОК ПЕРИОДА И ФИЛЬТРОВ ═══════════════ */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          2. Период и типы срабатываний
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <TextField
              label="Дата с"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              label="Дата по"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Типы срабатываний (все — если не выбрано):
            </Typography>
            <FormGroup row>
              {SIGNAL_TYPES.map((st) => (
                <FormControlLabel
                  key={st.value}
                  control={
                    <Checkbox
                      size="small"
                      checked={selectedSignalTypes.includes(st.value)}
                      onChange={() => handleToggleSignalType(st.value)}
                    />
                  }
                  label={<Typography variant="caption">{st.label}</Typography>}
                />
              ))}
            </FormGroup>
          </Grid>
        </Grid>
      </Paper>

      {/* ═══════════════ БЛОК ДАННЫХ ЗАЯВИТЕЛЯ ═══════════════ */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          3. Данные заявителя (необязательно)
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              label="Организация"
              size="small"
              fullWidth
              value={requesterOrg}
              onChange={(e) => setRequesterOrg(e.target.value)}
              placeholder="ООО «МГ-ФЛОТ»"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="ФИО заявителя"
              size="small"
              fullWidth
              value={requesterName}
              onChange={(e) => setRequesterName(e.target.value)}
              placeholder="Суверов М.Ю."
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Должность"
              size="small"
              fullWidth
              value={requesterPosition}
              onChange={(e) => setRequesterPosition(e.target.value)}
              placeholder="Руководитель службы безопасности"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Цель запроса"
              size="small"
              fullWidth
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              placeholder="Внутренняя служебная проверка"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* ═══════════════ КНОПКА ГЕНЕРАЦИИ ═══════════════ */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleGenerate}
          disabled={
            generating ||
            !dateFrom ||
            !dateTo ||
            (searchMode === 'vessels'
              ? selectedVessels.length === 0
              : !selectedOwner || ownerVessels.length === 0)
          }
          startIcon={generating ? <CircularProgress size={20} /> : <PictureAsPdfIcon />}
        >
          {generating ? 'Формирование отчёта...' : 'Сформировать отчёт'}
        </Button>

        <Button variant="outlined" onClick={handleToggleHistory} startIcon={<HistoryIcon />}>
          {showHistory ? 'Скрыть историю' : 'История отчётов'}
        </Button>
      </Box>

      {/* ═══════════════ РЕЗУЛЬТАТ ═══════════════ */}
      {result && (
        <Paper sx={{ p: 2, mb: 2, borderLeft: '4px solid #4caf50' }}>
          <Typography variant="h6" color="success.main" gutterBottom>
            Отчёт сформирован
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Период:
              </Typography>
              <Typography variant="body1">
                {result.period.from} — {result.period.to}
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Судов:
              </Typography>
              <Typography variant="body1">{result.totalVessels}</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Срабатываний:
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {result.totalActivations}
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<VisibilityIcon />}
                  onClick={() => previewPdf(result.reportId).catch((e) => setError(e.message))}
                  size="small"
                >
                  Просмотр
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={() => downloadPdf(result.reportId).catch((e) => setError(e.message))}
                  size="small"
                >
                  Скачать PDF
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<PrintIcon />}
                  onClick={() => printPdf(result.reportId).catch((e) => setError(e.message))}
                  size="small"
                >
                  Печать
                </Button>
              </Box>
            </Grid>
          </Grid>

          {/* Статистика по типам */}
          {Object.keys(result.byType).length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                По типам срабатываний:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {Object.entries(result.byType).map(([type, count]) => (
                  <Chip key={type} label={`${type}: ${count}`} size="small" variant="outlined" />
                ))}
              </Box>
            </Box>
          )}

          {/* Таблица по судам */}
          <TableContainer sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Судно</TableCell>
                  <TableCell>IMO</TableCell>
                  <TableCell>EN название</TableCell>
                  <TableCell align="right">Срабатываний</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.vessels.map((v) => (
                  <TableRow key={v.imo}>
                    <TableCell>{v.vesselName}</TableCell>
                    <TableCell>{v.imo}</TableCell>
                    <TableCell>{v.vesselNameEn || '—'}</TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={v.activationsCount > 0 ? 'bold' : 'normal'}>
                        {v.activationsCount}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* ═══════════════ ИСТОРИЯ ═══════════════ */}
      <Collapse in={showHistory}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            История сформированных отчётов
          </Typography>

          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : history.length === 0 ? (
            <Typography color="text.secondary">Отчёты не найдены</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Дата</TableCell>
                    <TableCell>Организация</TableCell>
                    <TableCell>Период</TableCell>
                    <TableCell>Судов</TableCell>
                    <TableCell>Срабатываний</TableCell>
                    <TableCell>Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((h) => (
                    <TableRow key={h.id}>
                      <TableCell>{h.id}</TableCell>
                      <TableCell>
                        {h.created_at ? new Date(h.created_at).toLocaleDateString('ru-RU') : '—'}
                      </TableCell>
                      <TableCell>{h.requester_org || '—'}</TableCell>
                      <TableCell>
                        {h.date_from ? new Date(h.date_from).toLocaleDateString('ru-RU') : '?'} —{' '}
                        {h.date_to ? new Date(h.date_to).toLocaleDateString('ru-RU') : '?'}
                      </TableCell>
                      <TableCell>
                        {Array.isArray(h.imo_numbers) ? h.imo_numbers.length : '?'}
                      </TableCell>
                      <TableCell>{h.total_activations ?? '—'}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            title="Просмотр"
                            onClick={() => previewPdf(h.id).catch((e) => setError(e.message))}
                          >
                            <VisibilityIcon color="primary" fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            title="Скачать PDF"
                            onClick={() => downloadPdf(h.id).catch((e) => setError(e.message))}
                          >
                            <PictureAsPdfIcon color="error" fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            title="Печать"
                            onClick={() => printPdf(h.id).catch((e) => setError(e.message))}
                          >
                            <PrintIcon color="secondary" fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Collapse>
    </Box>
  );
};

export default ShipOwnerReportPage;
